import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

interface FooterProps {
  darkMode: boolean;
}

const Footer = ({ darkMode }: FooterProps) => {
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();

  const footerLinks = {
    services: [
      { label: 'Software Development', path: '/services' },
      { label: 'IT Support', path: '/services' },
      { label: 'Digital Marketing', path: '/services' },
    ],
    company: [
      { label: t('nav.about'), path: '/about' },
      { label: t('nav.solutions'), path: '/solutions' },
      { label: t('nav.blog'), path: '/blog' },
      { label: t('nav.contact'), path: '/contact' },
    ],
  };

  const socialLinks = [
    { icon: 'ri-linkedin-fill', url: 'https://www.linkedin.com/company/logiqsoft', label: 'LinkedIn' },
    { icon: 'ri-facebook-fill', url: 'https://web.facebook.com/logiqsoft', label: 'Facebook' },
    { icon: 'ri-twitter-x-fill', url: 'https://twitter.com/LogiqSoft', label: 'Twitter' },
    { icon: 'ri-instagram-fill', url: 'https://www.instagram.com/logiqsoft/', label: 'Instagram' },
  ];

  return (
    <footer
      className={`relative ${
        darkMode
          ? 'bg-gradient-to-b from-gray-900 to-gray-950'
          : 'bg-gradient-to-b from-gray-50 to-white'
      } border-t ${darkMode ? 'border-gray-800' : 'border-gray-200'}`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
          {/* Company Info */}
          <div className="space-y-4">
            <Link to="/" className="inline-block">
              <img
                src={darkMode ? 'https://www.logiqsoft.io/assets/img/logo-blanc.png' : 'https://www.logiqsoft.io/assets/img/logo-bleu.png'}
                alt="LogiqSoft"
                className="h-10 w-auto"
              />
            </Link>
            <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              {t('footer.description')}
            </p>
            <div className="flex space-x-3">
              {socialLinks.map((social) => (
                <motion.a
                  key={social.label}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.1, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                  className={`w-10 h-10 flex items-center justify-center rounded-lg transition-colors duration-300 cursor-pointer ${
                    darkMode
                      ? 'bg-gray-800 text-gray-400 hover:bg-cyan-500 hover:text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-cyan-600 hover:text-white'
                  }`}
                  aria-label={social.label}
                >
                  <i className={`${social.icon} text-xl`}></i>
                </motion.a>
              ))}
            </div>
          </div>

          {/* Services */}
          <div>
            <h4 className={`font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              {t('footer.services')}
            </h4>
            <ul className="space-y-2">
              {footerLinks.services.map((link) => (
                <li key={link.label}>
                  <Link
                    to={link.path}
                    className={`text-sm transition-colors duration-300 hover:translate-x-1 inline-block whitespace-nowrap ${
                      darkMode
                        ? 'text-gray-400 hover:text-cyan-400'
                        : 'text-gray-600 hover:text-cyan-600'
                    }`}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h4 className={`font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              {t('footer.quickLinks')}
            </h4>
            <ul className="space-y-2">
              {footerLinks.company.map((link) => (
                <li key={link.label}>
                  <Link
                    to={link.path}
                    className={`text-sm transition-colors duration-300 hover:translate-x-1 inline-block whitespace-nowrap ${
                      darkMode
                        ? 'text-gray-400 hover:text-cyan-400'
                        : 'text-gray-600 hover:text-cyan-600'
                    }`}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className={`font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              {t('footer.contact')}
            </h4>
            <ul className="space-y-3">
              <li className="flex items-start space-x-3">
                <div className={`w-5 h-5 flex items-center justify-center mt-0.5 ${darkMode ? 'text-cyan-400' : 'text-cyan-600'}`}>
                  <i className="ri-map-pin-line text-lg"></i>
                </div>
                <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Lubumbashi, Kolwezi
                </span>
              </li>
              <li className="flex items-start space-x-3">
                <div className={`w-5 h-5 flex items-center justify-center mt-0.5 ${darkMode ? 'text-cyan-400' : 'text-cyan-600'}`}>
                  <i className="ri-mail-line text-lg"></i>
                </div>
                <a
                  href="mailto:services@logiqsoft.io"
                  className={`text-sm transition-colors duration-300 whitespace-nowrap ${
                    darkMode
                      ? 'text-gray-400 hover:text-cyan-400'
                      : 'text-gray-600 hover:text-cyan-600'
                  }`}
                >
                  services@logiqsoft.io
                </a>
              </li>
              <li className="flex items-start space-x-3">
                <div className={`w-5 h-5 flex items-center justify-center mt-0.5 ${darkMode ? 'text-cyan-400' : 'text-cyan-600'}`}>
                  <i className="ri-phone-line text-lg"></i>
                </div>
                <a
                  href="tel:+243972098720"
                  className={`text-sm transition-colors duration-300 whitespace-nowrap ${
                    darkMode
                      ? 'text-gray-400 hover:text-cyan-400'
                      : 'text-gray-600 hover:text-cyan-600'
                  }`}
                >
                  +243 972 098 720
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div
          className={`pt-8 border-t ${
            darkMode ? 'border-gray-800' : 'border-gray-200'
          } flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0`}
        >
          <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            © {currentYear} LogiqSoft. {t('footer.rights')}
          </p>
          <span
            className={`text-sm whitespace-nowrap ${
              darkMode ? 'text-gray-400' : 'text-gray-600'
            }`}
          >
            Powered by Logiqsoft
          </span>
        </div>
      </div>
    </footer>
  );
};

export default Footer;